/**
 * Country Selector Modal
 *
 * Welcoming modal that appears on first visit asking where the customer
 * is shopping from. This is required for proper currency display and
 * delivery calculation.
 *
 * Business Rule: Customers can auto-detect their country, but must be able to review
 * and override it at any time (especially before checkout).
 *
 * @module features/country/components/CountrySelectorModal
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input, Stack, ListItemButton, GradientIconContainer, SectionLabel, EmptyState } from '@/ui';
import { useTranslation } from 'react-i18next';
import { Globe, Search } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import type { CountryOption, CountrySelectorModalProps } from '../types';
import { useCountryCode, useCountryModalActions, useSetCountry, useShowCountryModal, } from '../hooks';

// Module-level animation constants to prevent re-renders
const globeAnimation = {
  initial: { scale: 0.8, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  transition: { duration: 0.3, ease: 'easeOut' },
} as const;

const countryItemAnimation = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 },
  transition: { duration: 0.2 },
} as const;

/**
 * Available countries with flag emojis
 *
 * Covers all Gelato-supported markets plus additional global markets.
 * Ordered: Primary markets first (Nordic + Major), then alphabetical
 *
 * Currency mapping based on GLOBAL_COMMERCE_SPECIFICATION.md
 */
const COUNTRIES: CountryOption[] = [
  // Primary Markets (Featured - Nordics + Top Markets)
  { code: 'SE', name: 'Sweden', flag: '🇸🇪', currency: 'SEK', isPrimary: true },
  { code: 'NO', name: 'Norway', flag: '🇳🇴', currency: 'NOK', isPrimary: true },
  { code: 'DK', name: 'Denmark', flag: '🇩🇰', currency: 'DKK', isPrimary: true },
  { code: 'FI', name: 'Finland', flag: '🇫🇮', currency: 'EUR', isPrimary: true },
  { code: 'US', name: 'United States', flag: '🇺🇸', currency: 'USD', isPrimary: true },
  { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', isPrimary: true },
  { code: 'DE', name: 'Germany', flag: '🇩🇪', currency: 'EUR', isPrimary: true },

  // Europe (Alphabetical)
  { code: 'AT', name: 'Austria', flag: '🇦🇹', currency: 'EUR', isPrimary: false },
  { code: 'BE', name: 'Belgium', flag: '🇧🇪', currency: 'EUR', isPrimary: false },
  { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', currency: 'BGN', isPrimary: false },
  { code: 'HR', name: 'Croatia', flag: '🇭🇷', currency: 'EUR', isPrimary: false },
  { code: 'CY', name: 'Cyprus', flag: '🇨🇾', currency: 'EUR', isPrimary: false },
  { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', currency: 'CZK', isPrimary: false },
  { code: 'EE', name: 'Estonia', flag: '🇪🇪', currency: 'EUR', isPrimary: false },
  { code: 'FR', name: 'France', flag: '🇫🇷', currency: 'EUR', isPrimary: false },
  { code: 'GR', name: 'Greece', flag: '🇬🇷', currency: 'EUR', isPrimary: false },
  { code: 'HU', name: 'Hungary', flag: '🇭🇺', currency: 'HUF', isPrimary: false },
  { code: 'IE', name: 'Ireland', flag: '🇮🇪', currency: 'EUR', isPrimary: false },
  { code: 'IT', name: 'Italy', flag: '🇮🇹', currency: 'EUR', isPrimary: false },
  { code: 'LV', name: 'Latvia', flag: '🇱🇻', currency: 'EUR', isPrimary: false },
  { code: 'LT', name: 'Lithuania', flag: '🇱🇹', currency: 'EUR', isPrimary: false },
  { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', currency: 'EUR', isPrimary: false },
  { code: 'MT', name: 'Malta', flag: '🇲🇹', currency: 'EUR', isPrimary: false },
  { code: 'NL', name: 'Netherlands', flag: '🇳🇱', currency: 'EUR', isPrimary: false },
  { code: 'PL', name: 'Poland', flag: '🇵🇱', currency: 'PLN', isPrimary: false },
  { code: 'PT', name: 'Portugal', flag: '🇵🇹', currency: 'EUR', isPrimary: false },
  { code: 'RO', name: 'Romania', flag: '🇷🇴', currency: 'RON', isPrimary: false },
  { code: 'SK', name: 'Slovakia', flag: '🇸🇰', currency: 'EUR', isPrimary: false },
  { code: 'SI', name: 'Slovenia', flag: '🇸🇮', currency: 'EUR', isPrimary: false },
  { code: 'ES', name: 'Spain', flag: '🇪🇸', currency: 'EUR', isPrimary: false },
  { code: 'CH', name: 'Switzerland', flag: '🇨🇭', currency: 'CHF', isPrimary: false },

  // North America
  { code: 'CA', name: 'Canada', flag: '🇨🇦', currency: 'CAD', isPrimary: false },
  { code: 'MX', name: 'Mexico', flag: '🇲🇽', currency: 'MXN', isPrimary: false },

  // South America
  { code: 'AR', name: 'Argentina', flag: '🇦🇷', currency: 'ARS', isPrimary: false },
  { code: 'BR', name: 'Brazil', flag: '🇧🇷', currency: 'BRL', isPrimary: false },
  { code: 'CL', name: 'Chile', flag: '🇨🇱', currency: 'CLP', isPrimary: false },
  { code: 'CO', name: 'Colombia', flag: '🇨🇴', currency: 'COP', isPrimary: false },

  // Asia-Pacific
  { code: 'AU', name: 'Australia', flag: '🇦🇺', currency: 'AUD', isPrimary: false },
  { code: 'CN', name: 'China', flag: '🇨🇳', currency: 'CNY', isPrimary: false },
  { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', currency: 'HKD', isPrimary: false },
  { code: 'IN', name: 'India', flag: '🇮🇳', currency: 'INR', isPrimary: false },
  { code: 'ID', name: 'Indonesia', flag: '🇮🇩', currency: 'IDR', isPrimary: false },
  { code: 'JP', name: 'Japan', flag: '🇯🇵', currency: 'JPY', isPrimary: false },
  { code: 'MY', name: 'Malaysia', flag: '🇲🇾', currency: 'MYR', isPrimary: false },
  { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', currency: 'NZD', isPrimary: false },
  { code: 'PH', name: 'Philippines', flag: '🇵🇭', currency: 'PHP', isPrimary: false },
  { code: 'SG', name: 'Singapore', flag: '🇸🇬', currency: 'SGD', isPrimary: false },
  { code: 'KR', name: 'South Korea', flag: '🇰🇷', currency: 'KRW', isPrimary: false },
  { code: 'TW', name: 'Taiwan', flag: '🇹🇼', currency: 'TWD', isPrimary: false },
  { code: 'TH', name: 'Thailand', flag: '🇹🇭', currency: 'THB', isPrimary: false },
  { code: 'VN', name: 'Vietnam', flag: '🇻🇳', currency: 'VND', isPrimary: false },

  // Middle East
  { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', currency: 'AED', isPrimary: false },
  { code: 'IL', name: 'Israel', flag: '🇮🇱', currency: 'ILS', isPrimary: false },
  { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', currency: 'SAR', isPrimary: false },

  // Africa
  { code: 'ZA', name: 'South Africa', flag: '🇿🇦', currency: 'ZAR', isPrimary: false },
];

/**
 * Country List Item Component
 */
interface CountryItemProps {
  country: CountryOption;
  isSelected: boolean;
  onClick: () => void;
}

function CountryItem({ country, isSelected, onClick }: CountryItemProps): React.ReactElement {
  return (
    <ListItemButton
      selected={isSelected}
      onClick={onClick}
      icon={country.flag}
      label={country.name}
      secondary={country.currency}
      aria-label={`${country.name}, ${country.currency}`}
    />
  );
}

/**
 * CountrySelectorModal Component
 *
 * Shows a welcoming modal asking customers where they're shopping from.
 * Required on first visit - cannot be dismissed without selection.
 */
export const CountrySelectorModal = React.memo(function CountrySelectorModal({
  open: controlledOpen,
  onClose,
  onSelect,
  isFirstTime,
}: CountrySelectorModalProps): React.ReactElement {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  // Store hooks
  const showModal = useShowCountryModal();
  const setCountry = useSetCountry();
  const { close } = useCountryModalActions();
  const activeCountryCode = useCountryCode();

  // Determine if modal is open (controlled or store-based)
  const isOpen = controlledOpen ?? showModal;

  // Use a key-based approach: when modal closes, reset by changing key
  // This avoids all setState patterns that trigger compiler warnings
  const modalKey = isOpen ? 'open' : 'closed';
  const [userOverride, setUserOverride] = useState<string | null>(null);

  // Derive selected code: user override if set, otherwise use active country
  const selectedCode = userOverride ?? activeCountryCode;

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!searchQuery.trim()) return COUNTRIES;
    const query = searchQuery.toLowerCase();
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.code.toLowerCase().includes(query) ||
        c.currency.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Separate primary and other countries
  const { primaryCountries, otherCountries } = useMemo(() => {
    const primary = filteredCountries.filter((c) => c.isPrimary);
    const other = filteredCountries.filter((c) => !c.isPrimary);
    return { primaryCountries: primary, otherCountries: other };
  }, [filteredCountries]);

  // Handle country selection
  const handleSelect = useCallback(
    (code: string): void => {
      setUserOverride(code);
    },
    []
  );

  // Handle continue button
  const handleContinue = useCallback((): void => {
    if (!selectedCode) return;

    const selectedCountry = COUNTRIES.find((country) => country.code === selectedCode);
    setCountry(selectedCode, {
      source: 'manual',
      name: selectedCountry?.name ?? null,
      currency: selectedCountry?.currency ?? null,
    });

    // Call external callbacks
    onSelect?.(selectedCode);
    onClose?.();
    close();
  }, [selectedCode, setCountry, onSelect, onClose, close]);

  // Handle close (only if not first time)
  const handleOpenChange = useCallback(
    (open: boolean): void => {
      if (!open) {
        onClose?.();
        close();
      }
    },
    [onClose, close]
  );

  // Reset component state when modal closes by using key
  // This is simpler than state synchronization and avoids compiler warnings
  return (
    <Dialog key={modalKey} open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <Stack gap={4} align="center">
          <motion.div
            initial={globeAnimation.initial}
            animate={globeAnimation.animate}
            transition={globeAnimation.transition}
          >
            <GradientIconContainer size="xl" shape="pill" variant="subtle">
              <Globe className="w-8 h-8 text-primary" />
            </GradientIconContainer>
          </motion.div>
          <DialogTitle>
            {isFirstTime ? t('ui.country.welcome') : t('ui.country.changeDeliveryCountry')}
          </DialogTitle>
          <DialogDescription>
            {t('ui.country.selectDeliveryCountry')}
          </DialogDescription>
        </Stack>

        {/* Search Input */}
        <Stack gap={4}>
          <Input
            type="text"
            id="country-search"
            placeholder={t('ui.country.searchCountries')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
            inputMode="search"
            autoCorrect="off"
            spellCheck={false}
            aria-label={t('ui.country.searchCountries')}
          />
        </Stack>

        {/* Country List */}
        <Stack gap={4} style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {/* Primary Markets */}
          {primaryCountries.length > 0 && (
            <div>
              {!searchQuery && (
                <SectionLabel>
                  {t('ui.country.popular')}
                </SectionLabel>
              )}
              <Stack gap={1}>
                <AnimatePresence mode="popLayout">
                  {primaryCountries.map((country) => (
                    <motion.div
                      key={country.code}
                      initial={countryItemAnimation.initial}
                      animate={countryItemAnimation.animate}
                      exit={countryItemAnimation.exit}
                      transition={countryItemAnimation.transition}
                    >
                      <CountryItem
                        country={country}
                        isSelected={selectedCode === country.code}
                        onClick={() => handleSelect(country.code)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Stack>
            </div>
          )}

          {/* Other Countries */}
          {otherCountries.length > 0 && (
            <div>
              {!searchQuery && primaryCountries.length > 0 && (
                <SectionLabel>
                  {t('ui.country.allCountries')}
                </SectionLabel>
              )}
              <Stack gap={1}>
                <AnimatePresence mode="popLayout">
                  {otherCountries.map((country) => (
                    <motion.div
                      key={country.code}
                      initial={countryItemAnimation.initial}
                      animate={countryItemAnimation.animate}
                      exit={countryItemAnimation.exit}
                      transition={countryItemAnimation.transition}
                    >
                      <CountryItem
                        country={country}
                        isSelected={selectedCode === country.code}
                        onClick={() => handleSelect(country.code)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </Stack>
            </div>
          )}

          {/* No Results */}
          {filteredCountries.length === 0 && (
            <EmptyState
              icon={Globe}
              description={t('ui.country.noCountriesFound', { query: searchQuery })}
            />
          )}
        </Stack>

        {/* Continue Button */}
        <Stack gap={4}>
          <Button
            variant="tangi"
            size="lg"
            fullWidth
            disabled={!selectedCode}
            onClick={handleContinue}
          >
            {t('ui.country.continueShopping')}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
});
